import React, { useState, useEffect } from 'react';
import { api } from '../api/client';

export default function PointsModal({ user, onClose, onDone }) {
  const [mode, setMode] = useState('add');
  const [grams, setGrams] = useState('');
  const [removeAmount, setRemoveAmount] = useState('');
  const [removeReason, setRemoveReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rates, setRates] = useState(null);

  useEffect(() => {
    api.get('/points/rates').then(setRates).catch(() => {});
  }, []);

  const pointsMap = rates?.points || {};
  const spinsMap = rates?.spins || {};
  const gramOptions = Object.keys(pointsMap)
    .sort((a, b) => parseFloat(a) - parseFloat(b))
    .map(g => ({ grams: g, points: pointsMap[g], spins: spinsMap[g] || 0 }));

  const selected = gramOptions.find(o => o.grams === grams);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (mode === 'add') {
        if (!grams) return;
        await api.post('/points/add', { userId: user.id, grams });
      } else {
        const pts = parseInt(removeAmount);
        if (!pts || pts <= 0) {
          setError('Enter a valid amount');
          setLoading(false);
          return;
        }
        await api.post('/points/remove', { userId: user.id, amount: pts, reason: removeReason });
      }
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>{mode === 'add' ? 'Drop' : 'Remove'} Bud Points — {user.username}</h3>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px' }}>
          Current balance: <strong style={{ color: 'var(--warning)' }}>{user.points} pts</strong>
        </p>

        {/* Mode toggle */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
          <button
            type="button"
            className={mode === 'add' ? 'btn-primary btn-small' : 'btn-secondary btn-small'}
            onClick={() => { setMode('add'); setError(''); }}
            style={{ flex: 1 }}
          >
            + Add Points
          </button>
          <button
            type="button"
            className={mode === 'remove' ? 'btn-primary btn-small' : 'btn-secondary btn-small'}
            onClick={() => { setMode('remove'); setError(''); }}
            style={{ flex: 1, ...(mode === 'remove' ? { background: 'var(--danger)' } : {}) }}
          >
            − Remove Points
          </button>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {mode === 'add' ? (
            <>
              <div className="form-group">
                <label>Pickup Weight</label>
                <select value={grams} onChange={e => setGrams(e.target.value)} required autoFocus>
                  <option value="">Select weight...</option>
                  {gramOptions.map(o => (
                    <option key={o.grams} value={o.grams}>{o.grams}g — {o.points} pts</option>
                  ))}
                </select>
              </div>
              {selected && (
                <div style={{
                  background: 'var(--bg-secondary)', padding: '12px', borderRadius: 'var(--radius-sm)',
                  marginBottom: 12, textAlign: 'center'
                }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--success)' }}>+{selected.points} pts</div>
                  {selected.spins > 0 && (
                    <div style={{ fontSize: 12, color: '#d6bcfa', marginTop: 4 }}>+ {selected.spins} spin token{selected.spins > 1 ? 's' : ''}</div>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="form-group">
                <label>Points to Remove</label>
                <input
                  type="number"
                  min="1"
                  max={user.points}
                  value={removeAmount}
                  onChange={e => setRemoveAmount(e.target.value)}
                  placeholder="e.g. 100"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Reason (optional)</label>
                <input
                  type="text"
                  value={removeReason}
                  onChange={e => setRemoveReason(e.target.value)}
                  placeholder="e.g. Refund, correction, etc."
                />
              </div>
              {removeAmount && parseInt(removeAmount) > 0 && (
                <div style={{
                  background: 'var(--bg-secondary)', padding: '12px', borderRadius: 'var(--radius-sm)',
                  marginBottom: 12, textAlign: 'center'
                }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--danger)' }}>−{removeAmount} pts</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    Balance after: {Math.max(0, user.points - parseInt(removeAmount))} pts
                  </div>
                </div>
              )}
            </>
          )}
          <div className="btn-row">
            <button
              type="submit"
              className={mode === 'add' ? 'btn-primary' : 'btn-danger'}
              disabled={loading || (mode === 'add' ? !grams : !removeAmount)}
            >
              {loading ? (mode === 'add' ? 'Dropping...' : 'Removing...') : (mode === 'add' ? 'Drop Points' : 'Remove Points')}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
